import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { api } from '../../lib/api';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  variant_attributes?: Record<string, string>;
  storeSlug?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  
  addItem: (item: CartItem, storeSlug?: string) => void;
  removeItem: (id: string, storeSlug?: string) => void;
  updateQuantity: (id: string, quantity: number, storeSlug?: string) => void;
  
  clear: () => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  syncWithBackend: (storeSlug: string) => Promise<void>;
  refreshCart: (storeSlug: string) => Promise<void>; // NUEVA FUNCIÓN
}

// Función auxiliar para transformar datos del Backend al formato del Frontend
const mapServerItemsToLocal = (serverItems: any[], storeSlug: string): CartItem[] => {
    if (!Array.isArray(serverItems)) return [];
    
    return serverItems.map((serverItem: any) => {
        let frontendId = String(serverItem.product);
        if (serverItem.variant) {
            frontendId = `${serverItem.product}-${serverItem.variant}`;
        }
        
        return {
           id: frontendId,
           name: serverItem.product_name || serverItem.name, 
           price: parseFloat(serverItem.price), 
           quantity: serverItem.quantity,
           image: serverItem.product_image || serverItem.image,
           variant_attributes: serverItem.variant_attributes,
           storeSlug: storeSlug
        };
    });
};

export const useCarrito = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      isOpen: false,
      
      addItem: async (item, storeSlug) => {
        set((state) => {
          const exists = state.items.find((i) => i.id === item.id && i.storeSlug === storeSlug);
          if (exists) {
            return {
              isOpen: true, 
              items: state.items.map((i) =>
                (i.id === item.id && i.storeSlug === storeSlug) 
                  ? { ...i, quantity: i.quantity + item.quantity } 
                  : i
              ),
            };
          }
          return { 
            isOpen: true,
            items: [...state.items, { ...item, storeSlug }] 
          };
        });

        const token = localStorage.getItem('tiendaya_token');
        if (token && storeSlug) {
            try {
                await api.post('/api/carrito/manage/add_item/', {
                    store_slug: storeSlug,
                    product_id: item.id,
                    quantity: item.quantity,
                    variant_attributes: item.variant_attributes
                });
            } catch (error) {
                console.error("Error sincronizando add_item:", error);
            }
        }
      },

      removeItem: async (id, storeSlug) => {
        set((state) => ({ 
            items: state.items.filter((item) => !(item.id === id && item.storeSlug === storeSlug)) 
        }));
        
        const token = localStorage.getItem('tiendaya_token');
        if (token && storeSlug) {
           try {
             const { data } = await api.post('/api/carrito/manage/remove_item/', {
               store_slug: storeSlug,
               item_id: id
             });

             if (data && Array.isArray(data.items)) {
                 const currentGlobalItems = get().items;
                 const otherStoresItems = currentGlobalItems.filter(i => i.storeSlug !== storeSlug);
                 const syncedStoreItems = mapServerItemsToLocal(data.items, storeSlug);
                 set({ items: [...otherStoresItems, ...syncedStoreItems] });
             } else if (data && data.items === []) {
                 const currentGlobalItems = get().items;
                 const otherStoresItems = currentGlobalItems.filter(i => i.storeSlug !== storeSlug);
                 set({ items: otherStoresItems });
             }
           } catch (error) { 
             console.error("Error sincronizando remove_item:", error); 
           }
        }
      },

      updateQuantity: async (id, quantity, storeSlug) => {
        set((state) => ({
          items: state.items.map((item) =>
            (item.id === id && item.storeSlug === storeSlug) 
                ? { ...item, quantity: quantity } 
                : item
          ),
        }));

        const token = localStorage.getItem('tiendaya_token');
        if (token && storeSlug) {
           try {
             await api.post('/api/carrito/manage/update_quantity/', {
               store_slug: storeSlug,
               item_id: id,
               quantity: quantity
             });
           } catch (error) { 
             console.error("Error sincronizando update_quantity:", error); 
           }
        }
      },

      clear: () => set({ items: [] }),

      openCart: () => set({ isOpen: true }),
      closeCart: () => set({ isOpen: false }),
      toggleCart: () => set((state) => ({ isOpen: !state.isOpen })),

      // PUSH: Envía local al servidor (Usado al agregar items)
      syncWithBackend: async (storeSlug) => {
        const currentGlobalItems = get().items;
        const itemsForThisStore = currentGlobalItems.filter(i => i.storeSlug === storeSlug);

        try {
          const payload = {
            store_slug: storeSlug,
            items: itemsForThisStore.map(item => ({
              id: item.id,
              quantity: item.quantity,
              variant_attributes: item.variant_attributes
            }))
          };
          
          const { data } = await api.post('/api/carrito/manage/sync/', payload);
          
          if (data && Array.isArray(data.items)) {
             const syncedStoreItems = mapServerItemsToLocal(data.items, storeSlug);
             const otherStoresItems = currentGlobalItems.filter(i => i.storeSlug !== storeSlug);
             set({ items: [...otherStoresItems, ...syncedStoreItems] });
          }
        } catch (error) { console.error("Error sync:", error); }
      },

      // PULL: Descarga la verdad del servidor (Usado al recargar página)
      refreshCart: async (storeSlug) => {
        try {
            // Traemos TODOS los carritos globales del usuario
            const { data } = await api.get('/api/carrito/manage/global/');
            
            if (Array.isArray(data)) {
                 // Buscamos el carrito de ESTA tienda específica
                 const serverCart = data.find((c: any) => c.store_slug === storeSlug);
                 
                 // Preservamos los items de OTRAS tiendas que tengamos en memoria
                 const currentGlobalItems = get().items;
                 const otherStoresItems = currentGlobalItems.filter(i => i.storeSlug !== storeSlug);

                 let newItems: CartItem[] = [];
                 // Si el servidor tiene items para esta tienda, los convertimos
                 if (serverCart && Array.isArray(serverCart.items)) {
                     newItems = mapServerItemsToLocal(serverCart.items, storeSlug);
                 }

                 // SOBREESCRIBIMOS el estado local de esta tienda con lo que diga el servidor
                 // Si el servidor dice "vacío", newItems será [], y los zombies morirán.
                 set({ items: [...otherStoresItems, ...newItems] });
                 console.log(`Carrito refrescado desde BD para ${storeSlug}`);
            }
        } catch (e) { console.error("Error refreshing cart:", e); }
      }
    }),
    {
      name: 'carrito-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ items: state.items }),
    }
  )
);